/**
 * Comprehensive tile configurations for Build the Stack
 *
 * Each tile represents a real-world system component with professional
 * experience context, performance characteristics, and architectural metadata.
 */

import type { FailureType, Tile, TileEffect, TileType } from '../types'

/**
 * Tile configuration helper function
 */
const createTile = (
  id: string,
  type: TileType,
  name: string,
  description: string,
  maxThroughput: number,
  latencyRange: [number, number],
  failureChance: number,
  failureTypes: FailureType[],
  effects: TileEffect[],
  resumeText: string,
  recoveryTime: number,
  visual: { icon: string; color: string; category: string },
  ports: { inputs: number; outputs: number }
): Tile => ({
  id,
  type,
  name,
  description,
  maxThroughput,
  latencyRange,
  failureChance,
  failureTypes,
  effects,
  resumeText,
  recoveryTime,
  visual,
  ports,
})

/**
 * Comprehensive tile definitions organized by category
 */

// ================================
// COMPUTE TIER
// ================================

const COMPUTE_TILES: Tile[] = [
  createTile(
    'nginx-web-server',
    'compute',
    'NGINX Web Server',
    'High-performance HTTP server and reverse proxy',
    2000,
    [1, 5],
    0.001,
    ['timeout', 'overload'],
    ['load-balancing', 'caching'],
    'Configured NGINX clusters handling 50M+ daily requests with 99.9% uptime',
    3000,
    { icon: '🌐', color: '#10b981', category: 'Web Server' },
    { inputs: 1, outputs: 2 }
  ),

  createTile(
    'nodejs-api',
    'compute',
    'Node.js API Service',
    'Scalable JavaScript runtime for backend services',
    1500,
    [10, 50],
    0.002,
    ['crash', 'overload', 'dependency'],
    ['autoscaling', 'retry-logic'],
    'Built microservices architecture serving 10M+ API calls daily',
    5000,
    { icon: '⚡', color: '#3b82f6', category: 'Application Server' },
    { inputs: 2, outputs: 3 }
  ),

  createTile(
    'docker-container',
    'compute',
    'Docker Container',
    'Containerized application deployment platform',
    1000,
    [5, 25],
    0.001,
    ['crash', 'dependency'],
    ['autoscaling', 'monitoring'],
    'Containerized 200+ services reducing deployment time by 80%',
    2000,
    { icon: '🐳', color: '#00d4ff', category: 'Container' },
    { inputs: 1, outputs: 1 }
  ),

  createTile(
    'lambda-function',
    'compute',
    'AWS Lambda',
    'Serverless compute for event-driven applications',
    5000,
    [100, 1000],
    0.005,
    ['timeout', 'dependency'],
    ['autoscaling'],
    'Implemented serverless architecture reducing infrastructure costs by 60%',
    1000,
    { icon: '⚡', color: '#f59e0b', category: 'Serverless' },
    { inputs: 1, outputs: 2 }
  ),

  createTile(
    'kubernetes-pod',
    'compute',
    'Kubernetes Pod',
    'Container orchestration with automatic scaling',
    800,
    [10, 30],
    0.003,
    ['crash', 'overload', 'network'],
    ['autoscaling', 'monitoring', 'retry-logic'],
    'Orchestrated 500+ pod cluster with zero-downtime deployments',
    8000,
    { icon: '☸️', color: '#326ce5', category: 'Orchestration' },
    { inputs: 1, outputs: 2 }
  ),
]

// ================================
// DATABASE TIER
// ================================

const DATABASE_TILES: Tile[] = [
  createTile(
    'postgresql-primary',
    'database',
    'PostgreSQL Primary',
    'Reliable ACID-compliant relational database',
    800,
    [5, 25],
    0.001,
    ['timeout', 'overload'],
    ['backup', 'monitoring'],
    'Optimized PostgreSQL clusters handling 100TB+ data with sub-5ms queries',
    10000,
    { icon: '🐘', color: '#336791', category: 'SQL Database' },
    { inputs: 3, outputs: 1 }
  ),

  createTile(
    'mongodb-cluster',
    'database',
    'MongoDB Cluster',
    'NoSQL document database with horizontal scaling',
    1200,
    [8, 40],
    0.002,
    ['timeout', 'overload', 'network'],
    ['autoscaling', 'backup'],
    'Architected MongoDB sharded clusters scaling to 10M+ documents',
    8000,
    { icon: '🍃', color: '#10b981', category: 'NoSQL Database' },
    { inputs: 2, outputs: 1 }
  ),

  createTile(
    'redis-cache',
    'cache',
    'Redis Cache',
    'In-memory data structure store for high-speed caching',
    10000,
    [1, 3],
    0.001,
    ['crash', 'overload'],
    ['caching'],
    'Implemented Redis caching reducing database load by 70%',
    2000,
    { icon: '⚡', color: '#dc2626', category: 'Cache' },
    { inputs: 2, outputs: 2 }
  ),

  createTile(
    'elasticsearch',
    'database',
    'Elasticsearch',
    'Distributed search and analytics engine',
    600,
    [20, 100],
    0.003,
    ['overload', 'network'],
    ['autoscaling', 'monitoring'],
    'Built real-time search across 1B+ documents with <100ms response',
    15000,
    { icon: '🔍', color: '#f59e0b', category: 'Search Engine' },
    { inputs: 1, outputs: 1 }
  ),
]

// ================================
// ROUTING & NETWORKING
// ================================

const ROUTING_TILES: Tile[] = [
  createTile(
    'aws-alb',
    'load-balancer',
    'AWS Load Balancer',
    'Application load balancer with SSL termination',
    5000,
    [1, 5],
    0.0005,
    ['timeout', 'network'],
    ['load-balancing', 'monitoring'],
    'Configured multi-AZ load balancers handling 100K+ concurrent connections',
    3000,
    { icon: '⚖️', color: '#ff9500', category: 'Load Balancer' },
    { inputs: 1, outputs: 4 }
  ),

  createTile(
    'api-gateway',
    'api-gateway',
    'API Gateway',
    'Centralized API management with authentication',
    3000,
    [5, 15],
    0.001,
    ['timeout', 'overload', 'dependency'],
    ['retry-logic', 'monitoring', 'circuit-breaker'],
    'Designed API gateway serving 50M+ requests with rate limiting and auth',
    5000,
    { icon: '🚪', color: '#8b5cf6', category: 'API Gateway' },
    { inputs: 1, outputs: 5 }
  ),

  createTile(
    'cloudflare-cdn',
    'routing',
    'Cloudflare CDN',
    'Global content delivery network',
    50000,
    [10, 50],
    0.0001,
    ['network'],
    ['caching', 'load-balancing'],
    'Implemented global CDN reducing page load times by 60%',
    1000,
    { icon: '🌍', color: '#f48120', category: 'CDN' },
    { inputs: 1, outputs: 1 }
  ),

  createTile(
    'consul-service-mesh',
    'routing',
    'Consul Service Mesh',
    'Service discovery and mesh networking',
    2000,
    [3, 10],
    0.002,
    ['network', 'dependency'],
    ['monitoring', 'encryption'],
    'Implemented service mesh for 200+ microservices with automatic discovery',
    7000,
    { icon: '🕸️', color: '#ca2465', category: 'Service Mesh' },
    { inputs: 2, outputs: 3 }
  ),
]

// ================================
// MONITORING & OBSERVABILITY
// ================================

const MONITORING_TILES: Tile[] = [
  createTile(
    'prometheus-metrics',
    'monitoring',
    'Prometheus',
    'Time-series monitoring and alerting system',
    1000,
    [5, 20],
    0.001,
    ['overload', 'network'],
    ['monitoring'],
    'Built comprehensive monitoring collecting 10M+ metrics per minute',
    5000,
    { icon: '📊', color: '#e6522c', category: 'Metrics' },
    { inputs: 5, outputs: 1 }
  ),

  createTile(
    'grafana-dashboard',
    'monitoring',
    'Grafana Dashboard',
    'Visualization and alerting for operational metrics',
    500,
    [50, 200],
    0.002,
    ['dependency', 'overload'],
    ['monitoring'],
    'Created 50+ operational dashboards with real-time alerting',
    3000,
    { icon: '📈', color: '#f46800', category: 'Visualization' },
    { inputs: 3, outputs: 1 }
  ),

  createTile(
    'elk-logging',
    'monitoring',
    'ELK Stack',
    'Centralized logging with Elasticsearch, Logstash, Kibana',
    800,
    [20, 100],
    0.003,
    ['overload', 'network'],
    ['monitoring'],
    'Centralized logging processing 1TB+ daily across distributed systems',
    10000,
    { icon: '📝', color: '#005571', category: 'Logging' },
    { inputs: 10, outputs: 1 }
  ),

  createTile(
    'jaeger-tracing',
    'monitoring',
    'Jaeger Tracing',
    'Distributed tracing for microservices',
    1200,
    [10, 50],
    0.002,
    ['overload', 'network'],
    ['monitoring'],
    'Implemented distributed tracing reducing debugging time by 80%',
    6000,
    { icon: '🔗', color: '#60dafb', category: 'Tracing' },
    { inputs: 8, outputs: 1 }
  ),
]

// ================================
// SECURITY & AUTHENTICATION
// ================================

const SECURITY_TILES: Tile[] = [
  createTile(
    'keycloak-auth',
    'security',
    'Keycloak SSO',
    'Identity and access management solution',
    1000,
    [20, 100],
    0.002,
    ['timeout', 'dependency'],
    ['encryption', 'monitoring'],
    'Implemented enterprise SSO for 10K+ users with SAML/OAuth integration',
    8000,
    { icon: '🔐', color: '#4c1d95', category: 'Authentication' },
    { inputs: 2, outputs: 1 }
  ),

  createTile(
    'vault-secrets',
    'security',
    'HashiCorp Vault',
    'Secrets management and encryption',
    500,
    [10, 50],
    0.001,
    ['timeout', 'network'],
    ['encryption', 'monitoring'],
    'Secured infrastructure secrets with automated rotation and audit trails',
    5000,
    { icon: '🏦', color: '#000000', category: 'Secrets Management' },
    { inputs: 1, outputs: 2 }
  ),

  createTile(
    'waf-firewall',
    'security',
    'Web Application Firewall',
    'Layer 7 security filtering and threat protection',
    10000,
    [1, 3],
    0.0005,
    ['overload'],
    ['monitoring'],
    'Configured WAF blocking 99.9% of malicious traffic with minimal latency',
    2000,
    { icon: '🛡️', color: '#dc2626', category: 'Firewall' },
    { inputs: 1, outputs: 1 }
  ),
]

// ================================
// QUEUE & MESSAGE SYSTEMS
// ================================

const QUEUE_TILES: Tile[] = [
  createTile(
    'kafka-stream',
    'queue',
    'Apache Kafka',
    'High-throughput distributed streaming platform',
    50000,
    [2, 10],
    0.001,
    ['network', 'overload'],
    ['monitoring', 'backup'],
    'Architected Kafka clusters processing 1M+ events per second',
    10000,
    { icon: '🌊', color: '#231f20', category: 'Event Streaming' },
    { inputs: 5, outputs: 10 }
  ),

  createTile(
    'rabbitmq-queue',
    'queue',
    'RabbitMQ',
    'Reliable message broker with delivery guarantees',
    5000,
    [5, 25],
    0.002,
    ['crash', 'overload'],
    ['retry-logic', 'monitoring'],
    'Implemented message queuing handling 10M+ messages daily with zero loss',
    7000,
    { icon: '🐰', color: '#ff6600', category: 'Message Queue' },
    { inputs: 3, outputs: 3 }
  ),

  createTile(
    'sqs-queue',
    'queue',
    'Amazon SQS',
    'Fully managed message queuing service',
    10000,
    [10, 100],
    0.001,
    ['timeout', 'dependency'],
    ['autoscaling', 'retry-logic'],
    'Scaled serverless queuing to handle traffic spikes with automatic scaling',
    3000,
    { icon: '📬', color: '#ff9900', category: 'Cloud Queue' },
    { inputs: 2, outputs: 2 }
  ),
]

// ================================
// STORAGE SYSTEMS
// ================================

const STORAGE_TILES: Tile[] = [
  createTile(
    's3-bucket',
    'storage',
    'Amazon S3',
    'Scalable object storage with 99.999999999% durability',
    1000,
    [50, 200],
    0.0001,
    ['network', 'timeout'],
    ['backup', 'monitoring'],
    'Architected S3 data lakes storing 100PB+ with intelligent tiering',
    1000,
    { icon: '🪣', color: '#ff9900', category: 'Object Storage' },
    { inputs: 2, outputs: 1 }
  ),

  createTile(
    'minio-storage',
    'storage',
    'MinIO Storage',
    'High-performance object storage compatible with S3 API',
    800,
    [20, 80],
    0.001,
    ['network', 'overload'],
    ['backup', 'monitoring'],
    'Deployed on-premise object storage reducing cloud costs by 40%',
    5000,
    { icon: '📦', color: '#c72e49', category: 'Object Storage' },
    { inputs: 2, outputs: 1 }
  ),
]

/**
 * Master tile configuration registry
 */
export const TILE_CONFIGS: Record<string, Tile> = {
  // Compute
  ...Object.fromEntries(COMPUTE_TILES.map(tile => [tile.id, tile])),

  // Database
  ...Object.fromEntries(DATABASE_TILES.map(tile => [tile.id, tile])),

  // Routing
  ...Object.fromEntries(ROUTING_TILES.map(tile => [tile.id, tile])),

  // Monitoring
  ...Object.fromEntries(MONITORING_TILES.map(tile => [tile.id, tile])),

  // Security
  ...Object.fromEntries(SECURITY_TILES.map(tile => [tile.id, tile])),

  // Queue
  ...Object.fromEntries(QUEUE_TILES.map(tile => [tile.id, tile])),

  // Storage
  ...Object.fromEntries(STORAGE_TILES.map(tile => [tile.id, tile])),
}

/**
 * Tiles organized by category for palette display
 */
export const TILES_BY_CATEGORY = {
  compute: COMPUTE_TILES,
  database: [
    ...DATABASE_TILES,
    ...DATABASE_TILES.filter(t => t.type === 'cache'),
  ],
  routing: ROUTING_TILES,
  monitoring: MONITORING_TILES,
  security: SECURITY_TILES,
  queue: QUEUE_TILES,
  storage: STORAGE_TILES,
}

/**
 * Get all available tiles as array
 */
export const getAllTiles = (): Tile[] => Object.values(TILE_CONFIGS)

/**
 * Get tiles by category
 */
export const getTilesByCategory = (category: string): Tile[] => {
  return TILES_BY_CATEGORY[category as keyof typeof TILES_BY_CATEGORY] || []
}

/**
 * Get tile by ID
 */
export const getTileById = (id: string): Tile | undefined => {
  return TILE_CONFIGS[id]
}

/**
 * Get tiles by type
 */
export const getTilesByType = (type: TileType): Tile[] => {
  return getAllTiles().filter(tile => tile.type === type)
}
